import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

export interface ClientPreferences {
  client_id: string;
  theme: 'dark' | 'light' | 'system';
  accent_color: 'green' | 'blue' | 'purple' | 'orange' | 'pink' | 'cyan';
  density: 'compact' | 'comfortable';
  avatar_type: 'initials' | 'preset' | 'custom';
  avatar_preset: string | null;
  avatar_url: string | null;
  reminder_payments: boolean;
  reminder_balance_alert: boolean;
  reminder_balance_threshold_minor: number;
  reminder_fx_alerts: boolean;
  reminder_fx_pairs: string[];
  reminder_fx_target_rates: Record<string, number>;
}

const DEFAULT_PREFS: Omit<ClientPreferences, 'client_id'> = {
  theme: 'dark',
  accent_color: 'green',
  density: 'comfortable',
  avatar_type: 'initials',
  avatar_preset: null,
  avatar_url: null,
  reminder_payments: true,
  reminder_balance_alert: true,
  reminder_balance_threshold_minor: 10000,
  reminder_fx_alerts: false,
  reminder_fx_pairs: [],
  reminder_fx_target_rates: {},
};

export function useClientPreferences() {
  const { profile } = useCustomerAuth();
  const qc = useQueryClient();
  const clientId = profile?.id;

  const query = useQuery({
    queryKey: ['client-preferences', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('client_preferences')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return { client_id: clientId!, ...DEFAULT_PREFS };
      return data as ClientPreferences;
    },
  });

  const mutation = useMutation({
    mutationFn: async (updates: Partial<ClientPreferences>) => {
      if (!clientId) throw new Error('No client');
      // Merge with current preferences to avoid DB defaults overwriting existing values on upsert
      const current = query.data ?? { client_id: clientId, ...DEFAULT_PREFS };
      const merged = { ...current, ...updates, client_id: clientId };
      const { error } = await (supabase as any)
        .from('client_preferences')
        .upsert(merged, { onConflict: 'client_id' });
      if (error) throw error;
    },
    onMutate: async (updates) => {
      // Optimistic update to prevent flicker
      await qc.cancelQueries({ queryKey: ['client-preferences', clientId] });
      const previous = qc.getQueryData(['client-preferences', clientId]);
      qc.setQueryData(['client-preferences', clientId], (old: any) => ({
        ...(old ?? { client_id: clientId, ...DEFAULT_PREFS }),
        ...updates,
      }));
      return { previous };
    },
    onError: (_err, _updates, context) => {
      if (context?.previous) {
        qc.setQueryData(['client-preferences', clientId], context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['client-preferences', clientId] });
    },
  });

  return {
    preferences: query.data ?? { client_id: clientId ?? '', ...DEFAULT_PREFS },
    isLoading: query.isLoading,
    update: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
}
